import { ApiError, api } from '@/core/api/request'
import { logger } from '@/core/app/logger'
import {
  OcrStampAcquireErrorBodySchema,
  QuizAnswerResponseSchema,
  StampAcquireResponseSchema,
  StampGetResponseSchema,
  StampQuizListResponseSchema,
} from '@/schemas/v2'
import type { QuizAnswerResponse, StampAcquireResponse, StampGetResponse, StampQuizListResponse } from '@/schemas/v2'

const BASE = process.env.NEXT_PUBLIC_API_URL ?? ''

type FetchOptions = { headers?: Record<string, string> }

export async function fetchStamp(stampId: number, options?: FetchOptions): Promise<StampGetResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return StampGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchStampQuizzes(stampId: number, options?: FetchOptions): Promise<StampQuizListResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}/quizzes`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return StampQuizListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postStamp(
  stampId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<StampAcquireResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return StampAcquireResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/**
 * XState 向けの POST。エラー時は ApiError(status, code) が throw される。
 * 成功レスポンスは使用しないため Promise<void> で返す。
 */
export async function postStampStrict(
  stampId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<void> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    await api.post<void>(url, body ?? {}, options)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postStampQuizAnswer(
  stampId: number,
  quizId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<QuizAnswerResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}/quizzes/${quizId}/answer`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return QuizAnswerResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/**
 * OCR スタンプ獲得失敗（読み取り不能）を表すエラー。
 * XState の onError ガードで name === 'OcrStampError' を判定する。
 */
export class OcrStampError extends Error {
  constructor(
    public readonly ocrId: number | null,
    public readonly code?: string,
    public readonly maxRetry: number | null = null,
  ) {
    super('OCR stamp acquisition failed')
    this.name = 'OcrStampError'
  }
}

/**
 * OCR 読み取り失敗として再撮影／補填申請ファネル（ocrReadFailure）に乗せるエラーコード（#3694）。
 * バックエンドはこれらのみ `error.ocrId`（スキャン TX ID）を返し、補填申請の対象になる。
 * 重複（OCR_DUPLICATION）・上限・期間外・画質などは含めない。それらは撮り直しても解決しない、
 * または message 自体が撮り方を案内しているため、QR/GPS と同じ acquireError で理由を表示する。
 */
export const OCR_READ_FAILURE_CODES: ReadonlySet<string> = new Set([
  'OCR_EXTRACT_FAILED',
  'OCR_EXTRACT_EMPTY',
  'OCR_KEYWORDS_NOT_FOUND',
  'OCR_PARSE_FAILED',
])

/** 400 のエラーコードが「OCR 読み取り失敗」か（ocrReadFailure に流すべきか） */
export const isOcrReadFailureCode = (code: string | undefined): boolean =>
  code !== undefined && OCR_READ_FAILURE_CODES.has(code)

/**
 * OCR スタンプ獲得。
 * - 成功: StampAcquireResponse を返す
 * - 400 かつ読み取り失敗系コード（OCR_READ_FAILURE_CODES）: OcrStampError を throw（ocrId / maxRetry を含む）
 * - それ以外（重複・上限・期間外・画質・400 以外）: ApiError をそのまま throw
 *   → 状態機械の acquireError に流れ、エラーコードに応じた理由を表示する（#3694）
 */
export async function postOcrStamp(
  stampId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<StampAcquireResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'OCR stamp fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return StampAcquireResponseSchema.parse(data)
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) {
      const parsed = OcrStampAcquireErrorBodySchema.safeParse(e.body)
      const data = parsed.success ? parsed.data : undefined
      // バックエンドは { error: { ocrId, code, maxRetry } } でネストして返す（トップレベルも後方互換で許容）
      const code = data?.error?.code ?? data?.code ?? e.code
      if (isOcrReadFailureCode(code)) {
        const ocrId = data?.error?.ocrId ?? data?.ocrId ?? null
        const maxRetry = data?.error?.maxRetry ?? data?.maxRetry ?? null
        logger.error({
          fetch_method: 'POST',
          fetch_url: url,
          msg: `OCR 読み取り失敗 code=${code} ocrId=${ocrId} maxRetry=${maxRetry}`,
        })
        throw new OcrStampError(ocrId, code, maxRetry)
      }
      // 重複・上限・期間外・画質など: 読み取り失敗ではないので ApiError のまま返す
      logger.error({ fetch_method: 'POST', fetch_url: url, msg: `OCR stamp 取得条件不一致 code=${code ?? 'unknown'}` })
      throw e
    }
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}
